import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  doc,
  getDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from './AuthContext';

export type TreasurerRole = 'treasurer' | 'worshipfulMaster' | 'auditor';

export interface UnitSummary {
  unitId: string;
  name: string;
  number: string;
  role: string;
}

export interface ActiveUnit {
  unitId: string;
  name: string;
  number: string;
  /** The user's unit-level role (e.g. Secretary). Distinct from treasurer roles. */
  unitRole: string;
}

export interface TreasurerSettingsView {
  isInitialised: boolean;
  treasurerName?: string;
  treasurerEmail?: string;
  worshipfulMasterName?: string;
  worshipfulMasterEmail?: string;
  financialYearStart?: { month: number; day: number };
  subscriptionDueDate?: { month: number; day: number };
  grandLodgeCapitationRatePence?: number;
  largeTransactionThresholdPence?: number;
  duesRemindersEnabled?: boolean;
  prorateSubscriptionsForMidYearLeavers?: boolean;
  meetingDates?: string[];
  /** Roles assigned to the current user (resolved client-side). */
  currentUserRoles: TreasurerRole[];
}

interface UnitContextValue {
  /** Units the current user is a member of (any role). */
  availableUnits: UnitSummary[];
  /** True while membership + unit documents are being resolved from Firestore. */
  availableUnitsLoading: boolean;
  /** The unit id resolved from the URL or state, if any. */
  activeUnit: ActiveUnit | null;
  setActiveUnitId: (unitId: string | null) => void;

  /** Loaded asynchronously; null while loading or if not initialised. */
  treasurerSettings: TreasurerSettingsView | null;
  /** True until the treasurerSettings doc has been read once. */
  treasurerSettingsLoading: boolean;

  /**
   * Convenience role checks. All return false until settings have loaded.
   */
  isTreasurer: boolean;
  isWorshipfulMaster: boolean;
  isAuditor: boolean;
  /** Any treasurer-module read access. */
  hasReadAccess: boolean;
}

const UnitContext = createContext<UnitContextValue | undefined>(undefined);

export function useUnitContext(): UnitContextValue {
  const ctx = useContext(UnitContext);
  if (!ctx) throw new Error('useUnitContext must be used within UnitProvider');
  return ctx;
}

interface UnitProviderProps {
  children: ReactNode;
  /** Optional unit id from the URL — drives active selection. */
  unitIdFromRoute?: string | null;
}

function membershipUnitId(entry: unknown): string | null {
  if (typeof entry === 'string' && entry.trim()) return entry.trim();
  if (!entry || typeof entry !== 'object') return null;
  const o = entry as Record<string, unknown>;
  if (typeof o.unitId === 'string' && o.unitId.trim()) return o.unitId.trim();
  if (typeof o.lodgeId === 'string' && o.lodgeId.trim()) return o.lodgeId.trim();
  return null;
}

function membershipRole(entry: unknown): string {
  if (!entry || typeof entry !== 'object') return 'Member';
  const r = (entry as Record<string, unknown>).role;
  return typeof r === 'string' && r ? r : 'Member';
}

function collectMemberships(
  userProfile: Record<string, unknown> | null | undefined,
): Map<string, string> {
  const roleByUnitId = new Map<string, string>();
  const ingest = (arr: unknown) => {
    if (!Array.isArray(arr)) return;
    for (const m of arr) {
      const id = membershipUnitId(m);
      if (id) roleByUnitId.set(id, membershipRole(m));
    }
  };
  if (!userProfile) return roleByUnitId;
  ingest(userProfile.units);
  ingest(userProfile.lodges);
  const solo = userProfile.unitId;
  if (typeof solo === 'string' && solo.trim()) {
    const id = solo.trim();
    if (!roleByUnitId.has(id)) roleByUnitId.set(id, 'Member');
  }
  return roleByUnitId;
}

export function UnitProvider({ children, unitIdFromRoute }: UnitProviderProps) {
  const { currentUser, userProfile } = useAuth();
  const [activeUnitId, setActiveUnitIdState] = useState<string | null>(null);
  const [availableUnits, setAvailableUnits] = useState<UnitSummary[]>([]);
  const [availableUnitsLoading, setAvailableUnitsLoading] = useState(true);
  const [treasurerSettings, setTreasurerSettings] = useState<TreasurerSettingsView | null>(null);
  const [treasurerSettingsLoading, setTreasurerSettingsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function resolveUnits() {
      if (!currentUser?.uid || !userProfile) {
        setAvailableUnits([]);
        setAvailableUnitsLoading(false);
        return;
      }

      setAvailableUnitsLoading(true);
      try {
        const profile = userProfile as unknown as Record<string, unknown>;
        const roleByUnitId = collectMemberships(profile);
        const ids = new Set(roleByUnitId.keys());

        const summaries: UnitSummary[] = [];
        for (const unitId of ids) {
          const unitDoc = await getDoc(doc(db, 'units', unitId));
          if (!unitDoc.exists()) continue;
          const data = unitDoc.data() as Record<string, unknown>;
          let role = roleByUnitId.get(unitId) ?? 'Member';
          const members = Array.isArray(data.members) ? data.members : [];
          const memberRoles =
            data.memberRoles && typeof data.memberRoles === 'object'
              ? (data.memberRoles as Record<string, unknown>)
              : {};

          const isMemberViaArray = members.some((m) => m === currentUser.uid);
          const isMemberViaRoles = memberRoles[currentUser.uid] !== undefined;
          if (!isMemberViaArray && !isMemberViaRoles) {
            // Only show lodges the user is actually signed to.
            continue;
          }

          const name = typeof data.name === 'string' && data.name ? data.name : 'Unnamed unit';
          const number =
            (typeof data.unitNumber === 'string' && data.unitNumber) ||
            (typeof data.number === 'string' && data.number) ||
            '';
          summaries.push({ unitId, name, number, role });
        }

        summaries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        if (!cancelled) setAvailableUnits(summaries);
      } catch (e) {
        console.error('Treasurer: failed to resolve unit memberships', e);
        if (!cancelled) setAvailableUnits([]);
      } finally {
        if (!cancelled) setAvailableUnitsLoading(false);
      }
    }

    void resolveUnits();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, userProfile]);

  // Resolve active unit id. URL takes priority.
  useEffect(() => {
    if (unitIdFromRoute && unitIdFromRoute !== activeUnitId) {
      setActiveUnitIdState(unitIdFromRoute);
    }
  }, [unitIdFromRoute, activeUnitId]);

  // Default to the first available unit if none selected.
  useEffect(() => {
    if (!activeUnitId && availableUnits.length === 1) {
      setActiveUnitIdState(availableUnits[0].unitId);
    }
  }, [availableUnits, activeUnitId]);

  const setActiveUnitId = useCallback((id: string | null) => {
    setActiveUnitIdState(id);
  }, []);

  const activeUnit = useMemo<ActiveUnit | null>(() => {
    if (!activeUnitId) return null;
    const summary = availableUnits.find((u) => u.unitId === activeUnitId);
    if (!summary) {
      // The user has the URL pointed at a unit they're not a member of.
      // Surface that as null; the route guard handles redirection.
      return null;
    }
    return {
      unitId: summary.unitId,
      name: summary.name,
      number: summary.number,
      unitRole: summary.role,
    };
  }, [activeUnitId, availableUnits]);

  // Subscribe to the treasurerSettings doc for the active unit.
  const settingsSubRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (settingsSubRef.current) {
      settingsSubRef.current();
      settingsSubRef.current = null;
    }
    if (!activeUnitId || !currentUser) {
      setTreasurerSettings(null);
      setTreasurerSettingsLoading(false);
      return;
    }
    setTreasurerSettingsLoading(true);
    const ref = doc(db, 'units', activeUnitId, 'treasurerSettings', 'config');
    settingsSubRef.current = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setTreasurerSettings({
            isInitialised: false,
            currentUserRoles: [],
          });
          setTreasurerSettingsLoading(false);
          return;
        }
        const data = (snap.data() ?? {}) as {
          isInitialised?: boolean;
          treasurerName?: string;
          treasurerEmail?: string;
          worshipfulMasterName?: string;
          worshipfulMasterEmail?: string;
          financialYearStart?: { month: number; day: number };
          subscriptionDueDate?: { month: number; day: number };
          grandLodgeCapitationRatePence?: number;
          largeTransactionThresholdPence?: number;
          duesRemindersEnabled?: boolean;
          prorateSubscriptionsForMidYearLeavers?: boolean;
          meetingDates?: string[];
          roleAssignments?: unknown[];
        };
        type RawRoleAssignment = {
          userId?: unknown;
          role?: unknown;
          expiresAt?: { toMillis?: () => number } | number | null;
        };
        const rawRoles: RawRoleAssignment[] = Array.isArray(data.roleAssignments)
          ? (data.roleAssignments as RawRoleAssignment[])
          : [];
        const roles: TreasurerRole[] = rawRoles
          .filter((r) => r.userId === currentUser.uid && typeof r.role === 'string')
          .filter((r) => {
            const exp = r.expiresAt;
            if (!exp) return true;
            const millis = typeof exp === 'object' && exp && typeof exp.toMillis === 'function'
              ? exp.toMillis()
              : typeof exp === 'number'
                ? exp
                : NaN;
            return Number.isFinite(millis) ? millis > Date.now() : true;
          })
          .map((r) => r.role as TreasurerRole);
        setTreasurerSettings({
          isInitialised: data.isInitialised === true,
          treasurerName: data.treasurerName,
          treasurerEmail: data.treasurerEmail,
          worshipfulMasterName: data.worshipfulMasterName,
          worshipfulMasterEmail: data.worshipfulMasterEmail,
          financialYearStart: data.financialYearStart,
          subscriptionDueDate: data.subscriptionDueDate,
          grandLodgeCapitationRatePence: data.grandLodgeCapitationRatePence,
          largeTransactionThresholdPence: data.largeTransactionThresholdPence,
          duesRemindersEnabled: data.duesRemindersEnabled,
          prorateSubscriptionsForMidYearLeavers: data.prorateSubscriptionsForMidYearLeavers,
          meetingDates: data.meetingDates,
          currentUserRoles: roles,
        });
        setTreasurerSettingsLoading(false);
      },
      (err) => {
        console.error('Failed to subscribe to treasurer settings', err);
        setTreasurerSettings(null);
        setTreasurerSettingsLoading(false);
      },
    );
    return () => {
      if (settingsSubRef.current) {
        settingsSubRef.current();
        settingsSubRef.current = null;
      }
    };
  }, [activeUnitId, currentUser]);

  const value = useMemo<UnitContextValue>(() => {
    const roles = treasurerSettings?.currentUserRoles ?? [];
    const isTreasurer = roles.includes('treasurer') || userProfile?.isAdmin === true;
    const isWorshipfulMaster = roles.includes('worshipfulMaster');
    const isAuditor = roles.includes('auditor');
    return {
      availableUnits,
      availableUnitsLoading,
      activeUnit,
      setActiveUnitId,
      treasurerSettings,
      treasurerSettingsLoading,
      isTreasurer,
      isWorshipfulMaster,
      isAuditor,
      hasReadAccess: isTreasurer || isWorshipfulMaster || isAuditor,
    };
  }, [
    availableUnits,
    availableUnitsLoading,
    activeUnit,
    setActiveUnitId,
    treasurerSettings,
    treasurerSettingsLoading,
    userProfile?.isAdmin,
  ]);

  return <UnitContext.Provider value={value}>{children}</UnitContext.Provider>;
}
